import { useEffect, useRef } from 'react'
import type { WireArticleFull } from '../api/types'

/**
 * Broadcasts live article draft state to any open live-preview window via
 * BroadcastChannel. Mirrors the pattern used by usePreviewBroadcast for the
 * page editor.
 *
 * The preview window sends `{ type: 'ready' }` on mount; this hook responds
 * immediately so the window doesn't have to wait for the next edit.
 * Subsequent changes are debounced at 150 ms.
 */
export function useArticlePreviewBroadcast(
  id: string,
  article: WireArticleFull | null,
) {
  const channelRef = useRef<BroadcastChannel | null>(null)
  const latestRef = useRef(article)
  latestRef.current = article

  useEffect(() => {
    if (!id) return
    const channel = new BroadcastChannel(`wire-ui:article-preview:${id}`)
    channelRef.current = channel

    channel.onmessage = (ev: MessageEvent) => {
      if (ev.data?.type === 'ready' && latestRef.current) {
        channel.postMessage({ type: 'state', article: latestRef.current })
      }
    }

    return () => {
      channel.close()
      channelRef.current = null
    }
  }, [id])

  useEffect(() => {
    if (!article) return
    const timer = setTimeout(() => {
      channelRef.current?.postMessage({ type: 'state', article })
    }, 150)
    return () => clearTimeout(timer)
  }, [article])
}
